import { useTheme } from '../contexts/ThemeContext';

function parseInline(text, isLight) {
  if (!text.includes('*')) return text;
  return text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i} className={isLight ? 'text-gray-900 font-semibold' : 'text-white font-semibold'}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*'))
      return <span key={i} className={isLight ? 'text-gray-600' : 'text-gray-300'}>{part.slice(1, -1)}</span>;
    return part;
  });
}

function ScoreLine({ line, isLight }) {
  const match = line.match(/^[\-\s]*(.+?):\s*(\d+)\/(\d+)\s*(.*)$/);
  if (!match) return <BulletLine line={line} isLight={isLight} />;
  const [, label, score, total, reason] = match;
  const pct = Math.round((parseInt(score) / parseInt(total)) * 100);
  const color = pct >= 70 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-3 py-1">
      <span className={`text-xs w-20 flex-shrink-0 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>{label}</span>
      <div className={`flex-1 rounded-full h-1.5 ${isLight ? 'bg-gray-200' : 'bg-gray-800'}`}>
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold w-8 text-right ${isLight ? 'text-gray-800' : 'text-white'}`}>{score}/{total}</span>
      {reason && <span className={`text-[11px] truncate max-w-[160px] ${isLight ? 'text-gray-400' : 'text-gray-500'}`}>{reason.replace(/^\(|\)$/g, '')}</span>}
    </div>
  );
}

function BulletLine({ line, isLight }) {
  const text = line.replace(/^[\-\*]\s*/, '');
  const labelMatch = text.match(/^\*\*([^*]+)\*\*[:：]\s*(.*)$/);
  if (labelMatch) {
    return (
      <div className="flex items-baseline gap-2 py-0.5">
        <span className={`text-xs font-bold flex-shrink-0 whitespace-nowrap min-w-[80px] ${isLight ? 'text-gray-700' : 'text-gray-200'}`}>
          {labelMatch[1]}
        </span>
        <span className={`text-xs ${isLight ? 'text-gray-400' : 'text-gray-400'}`}>·</span>
        <span className={`text-sm leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>{parseInline(labelMatch[2], isLight)}</span>
      </div>
    );
  }
  return (
    <div className="flex gap-2 items-start">
      <span className="text-brand text-xs mt-1.5 flex-shrink-0">▸</span>
      <span className={`text-sm leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>{parseInline(text, isLight)}</span>
    </div>
  );
}

function MarkdownTable({ lines, isLight }) {
  if (lines.length < 2) return null;
  const parseRow = (line) =>
    line.split('|').map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);

  const headers = parseRow(lines[0]);
  const rows = lines.slice(2).map(parseRow);

  return (
    <div className="overflow-x-auto my-2">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className={isLight ? 'bg-gray-100' : 'bg-gray-800'}>
            {headers.map((h, i) => (
              <th key={i} className={`px-3 py-2 text-left font-semibold text-xs whitespace-nowrap ${isLight ? 'text-gray-700 border border-gray-200' : 'text-gray-300 border border-gray-700'}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? (isLight ? 'bg-white' : 'bg-gray-900') : (isLight ? 'bg-gray-50' : 'bg-gray-800/50')}>
              {row.map((cell, ci) => (
                <td key={ci} className={`px-3 py-2 text-xs align-top leading-relaxed ${isLight ? 'text-gray-600 border border-gray-200' : 'text-gray-300 border border-gray-800'}`}>
                  {cell.replace(/<br\s*\/?>/gi, '\n').split('\n').map((t, ti, arr) => (
                    <span key={ti}>{parseInline(t.replace(/^[\-\*]\s*/, ''), isLight)}{ti < arr.length - 1 && <br />}</span>
                  ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ title, lines, isLight }) {
  if (title.includes('이미지 생성')) return null;

  const content = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) { i++; continue; }
    if (/^-{2,}$/.test(line) || /^\*{2,}$/.test(line)) { i++; continue; }

    if (line.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      content.push(<MarkdownTable key={`table-${i}`} lines={tableLines} isLight={isLight} />);
      continue;
    }

    if (line.startsWith('### ')) {
      content.push(
        <h3 key={i} className={`font-semibold text-sm mt-4 mb-2 flex items-center gap-2 ${isLight ? 'text-gray-700' : 'text-gray-200'}`}>
          <span className={`w-0.5 h-3.5 rounded-full ${isLight ? 'bg-gray-300' : 'bg-gray-600'}`} />
          {line.slice(4)}
        </h3>
      );
      i++; continue;
    }

    if (line.match(/^[\-\*]\s*.+:\s*\d+\/\d+/)) {
      const scoreLines = [];
      while (i < lines.length && lines[i].trim().match(/^[\-\*]\s*.+:\s*\d+\/\d+/)) {
        scoreLines.push(<ScoreLine key={i} line={lines[i].trim()} isLight={isLight} />);
        i++;
      }
      content.push(
        <div key={`scores-${i}`} className={`rounded-lg px-3 py-2 space-y-1 my-2 ${isLight ? 'bg-gray-100' : 'bg-gray-800/50'}`}>
          {scoreLines}
        </div>
      );
      continue;
    }

    if (line.match(/^[\-\*]\s/)) {
      const items = [];
      while (i < lines.length && lines[i].trim().match(/^[\-\*]\s/)) {
        items.push(<BulletLine key={i} line={lines[i].trim()} isLight={isLight} />);
        i++;
      }
      content.push(<div key={`list-${i}`} className="space-y-1.5 my-1">{items}</div>);
      continue;
    }

    if (line.startsWith('**')) {
      content.push(
        <p key={i} className="text-sm leading-relaxed mt-1">{parseInline(line, isLight)}</p>
      );
      i++; continue;
    }

    const paraLines = [];
    while (i < lines.length) {
      const l = lines[i].trim();
      if (!l || l.startsWith('#') || l.match(/^[\-\*]\s/) || l.startsWith('|') || /^-{2,}$/.test(l)) break;
      paraLines.push(l);
      i++;
    }
    if (paraLines.length > 0) {
      content.push(
        <p key={`p-${i}`} className={`text-sm leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
          {paraLines.map((l, j) => (
            <span key={j}>{parseInline(l, isLight)}{j < paraLines.length - 1 ? ' ' : ''}</span>
          ))}
        </p>
      );
    }
  }

  if (content.length === 0) return null;

  return (
    <div className={`rounded-xl border overflow-hidden ${isLight ? 'border-gray-200' : 'border-gray-800'}`}>
      <div className={`px-4 py-2.5 border-b ${isLight ? 'bg-gray-100 border-gray-200' : 'bg-gray-800/70 border-gray-800'}`}>
        <h2 className={`font-bold text-sm ${isLight ? 'text-gray-800' : 'text-white'}`}>{title}</h2>
      </div>
      <div className={`px-4 py-4 space-y-3 ${isLight ? 'bg-white' : 'bg-gray-900/50'}`}>
        {content}
      </div>
    </div>
  );
}

function RenderedAnalysis({ text, isLight }) {
  const sections = [];
  let currentTitle = null;
  let currentLines = [];

  text.split('\n').forEach(line => {
    if (line.startsWith('## ')) {
      if (currentTitle !== null) sections.push({ title: currentTitle, lines: currentLines });
      currentTitle = line.slice(3).trim();
      currentLines = [];
    } else {
      currentLines.push(line);
    }
  });
  if (currentTitle !== null) sections.push({ title: currentTitle, lines: currentLines });

  const introLines = sections.length === 0 ? currentLines : [];

  return (
    <div className="space-y-4">
      {introLines.filter(l => l.trim()).map((l, i) => (
        <p key={i} className={`text-sm leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>{parseInline(l, isLight)}</p>
      ))}
      {sections.map((s, i) => (
        <Section key={i} title={s.title} lines={s.lines} isLight={isLight} />
      ))}
    </div>
  );
}

export default function AnalysisRenderer({ text, streaming = false }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  if (!text) return null;

  if (streaming) {
    return (
      <pre className={`text-sm whitespace-pre-wrap font-sans leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>
        {text}
        <span className="inline-block w-1.5 h-4 bg-brand animate-pulse ml-0.5 align-middle" />
      </pre>
    );
  }

  return <RenderedAnalysis text={text} isLight={isLight} />;
}
